import { useState } from 'react';
import { useUsers, UserWithRoles } from '@/hooks/useUsers';
import { useAuth } from '@/hooks/useAuth';
import { useLeadDepartment, useDepartmentById } from '@/hooks/useConfig';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UserPlus, Search, Mail, Shield, ShieldCheck, Eye, EyeOff, Copy, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

function normalizeLeaderId(leader: { user?: { id: string }; id?: string }): string {
  return (leader as { user?: { id: string } }).user?.id ?? (leader as { id: string }).id ?? '';
}

export function UsersPage() {
  const { users, isLoading, createUser, updateUserStatus, deleteUser, reassignUserTickets, refetch } = useUsers();
  const { isAdmin, isTeamLeader, user: currentUser } = useAuth();
  const { leadDepartmentId } = useLeadDepartment();
  const { data: leadDepartment } = useDepartmentById(leadDepartmentId ?? undefined);
  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    position: '',
    role: 'agent' as 'admin' | 'agent' | 'team_leader',
  });
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [pendingStatusChange, setPendingStatusChange] = useState<{ user: UserWithRoles; newActive: boolean } | null>(null);
  const [deletingUser, setDeletingUser] = useState<UserWithRoles | null>(null);
  const [isDeleteUserOpen, setIsDeleteUserOpen] = useState(false);
  const [ticketsToReassign, setTicketsToReassign] = useState<Array<{ id: string; ticketNumber: string; description: string }>>([]);
  const [targetUserIdForReassign, setTargetUserIdForReassign] = useState<string>("");

  const departmentMemberIds = new Set<string>([]);
  if (leadDepartment) {
    (leadDepartment.agents ?? []).forEach((a) => departmentMemberIds.add((a as { id: string }).id));
    const leaders = leadDepartment.teamLeaders ?? (leadDepartment.teamLeader ? [leadDepartment.teamLeader] : []);
    leaders.forEach((l) => {
      const id = normalizeLeaderId(l as { user?: { id: string }; id?: string });
      if (id) departmentMemberIds.add(id);
    });
  }
  const baseFiltered = isTeamLeader && leadDepartmentId && leadDepartment
    ? users.filter((u) => departmentMemberIds.has(u.id) || (u.user_id && departmentMemberIds.has(u.user_id)))
    : users;
  const filteredUsers = baseFiltered.filter(
    (user) =>
      user.name.toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email) {
      toast.error('Name and email are required');
      return;
    }

    createUser.mutate(
      {
        email: newUser.email,
        name: newUser.name,
        position: newUser.position,
        role: newUser.role,
      },
      {
        onSuccess: (data) => {
          const password = (data as { temporary_password?: string }).temporary_password;
          if (password) {
            setTempPassword(password);
          } else {
            toast.success('User created successfully');
            setNewUser({ name: '', email: '', position: '', role: 'agent' as const });
            setIsCreating(false);
          }
        },
      }
    );
  };

  const handleCloseModal = (open: boolean) => {
    setIsCreating(open);
    if (!open) {
      setTempPassword(null);
        setNewUser({ name: '', email: '', position: '', role: 'agent' as const });
    }
  };

  const handleStatusChangeClick = (user: UserWithRoles, newActive: boolean) => {
    const isSelf = currentUser && (user.user_id === currentUser.id || user.id === currentUser.id);
    if (isSelf && !newActive) {
      toast.error("You cannot deactivate your own account");
      return;
    }
    setPendingStatusChange({ user, newActive });
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingStatusChange) return;
    await updateUserStatus(pendingStatusChange.user.user_id, pendingStatusChange.newActive);
    setPendingStatusChange(null);
  };

  const handleDeleteUserClick = (user: UserWithRoles) => {
    const isSelf = currentUser && (user.user_id === currentUser.id || user.id === currentUser.id);
    if (isSelf) {
      toast.error("You cannot delete your own account");
      return;
    }
    setDeletingUser(user);
    setTicketsToReassign([]);
    setTargetUserIdForReassign("");
    setIsDeleteUserOpen(true);
  };

  const handleConfirmDeleteUser = async () => {
    if (!deletingUser) return;

    const userId = deletingUser.user_id ?? deletingUser.id;
    try {
      const data = await deleteUser.mutateAsync(userId);
      if (data?.success) {
        setDeletingUser(null);
        setTicketsToReassign([]);
        setTargetUserIdForReassign("");
        setIsDeleteUserOpen(false);
      } else if (data?.requiresReassignment) {
        setTicketsToReassign(data.tickets ?? []);
        setTargetUserIdForReassign("");
      }
    } catch {
      // Error already shown by mutation
    }
  };

  const handleReassignAndDeleteUser = () => {
    if (!deletingUser || !targetUserIdForReassign || ticketsToReassign.length === 0) return;
    const fromId = deletingUser.user_id ?? deletingUser.id;

    reassignUserTickets.mutate(
      {
        fromUserId: fromId,
        toUserId: targetUserIdForReassign,
        ticketsIds: ticketsToReassign.map((t) => t.id),
      },
      {
        onSuccess: () => {
          setDeletingUser(null);
          setTicketsToReassign([]);
          setTargetUserIdForReassign("");
          setIsDeleteUserOpen(false);
        },
      }
    );
  };

  const targetUsersForReassign = users.filter(
    (u) => (u.user_id ?? u.id) !== (deletingUser?.user_id ?? deletingUser?.id)
  );

  if (!isAdmin && !isTeamLeader) {
    return (
      <div className="text-center py-12">
        <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
        <p className="text-muted-foreground">You need admin or team lead privileges to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{isTeamLeader ? 'Department Users' : 'User Management'}</h1>
          <p className="text-muted-foreground">{isTeamLeader ? 'Users in your department' : 'Manage tech team members and their roles'}</p>
        </div>
        {!isTeamLeader && (
        <Dialog open={isCreating} onOpenChange={handleCloseModal}>
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            {tempPassword ? (
              <div className="space-y-4 py-6">
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center text-green-600 mb-2">
                    <ShieldCheck className="h-6 w-6" />
                  </div>
                  <DialogTitle>User Created Successfully</DialogTitle>
                  <DialogDescription>
                    Please share this temporary password with the user. They will be required to change it on their first login.
                  </DialogDescription>
                </div>
                
                <div className="space-y-2 pt-2">
                  <Label>Temporary Password</Label>
                  <div className="bg-muted p-4 rounded-md font-mono text-lg font-bold break-all flex justify-between items-center border">
                    {tempPassword}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => {
                        navigator.clipboard.writeText(tempPassword);
                        toast.success('Password copied to clipboard');
                      }}
                      className="ml-2 h-8 w-8 p-0"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button className="w-full" onClick={() => handleCloseModal(false)}>
                    Close and Continue
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>Add New User</DialogTitle>
                  <DialogDescription>
                    Create a new tech team member. They will receive an activation email.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      placeholder="john@company.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="position">Position</Label>
                    <Input
                      id="position"
                      value={newUser.position}
                      onChange={(e) => setNewUser({ ...newUser, position: e.target.value })}
                      placeholder="Software Engineer"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="role"
                          checked={newUser.role === 'agent'}
                          onChange={() => setNewUser({ ...newUser, role: 'agent' })}
                          className="text-primary"
                        />
                        <span>Agent</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="role"
                          checked={newUser.role === 'team_leader'}
                          onChange={() => setNewUser({ ...newUser, role: 'team_leader' })}
                          className="text-primary"
                        />
                        <span>Team Leader</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="role"
                          checked={newUser.role === 'admin'}
                          onChange={() => setNewUser({ ...newUser, role: 'admin' })}
                          className="text-primary"
                        />
                        <span>Admin</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => handleCloseModal(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateUser}>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Invitation
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                {!isTeamLeader && <TableHead className="w-[100px]">Active</TableHead>}
                {!isTeamLeader && <TableHead className="w-[80px]">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isTeamLeader ? 5 : 7} className="text-center py-8 text-muted-foreground">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={user.avatar_url ?? undefined} />
                          <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{user.position || '—'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {(user.roles ?? []).map((role, idx) => {
                          const roleName = typeof role === 'string' ? role : (role?.role ?? '');
                          if (!roleName) return null;
                          return (
                            <Badge
                              key={(typeof role === 'object' && role?.id) ?? `role-${idx}`}
                              variant={roleName === 'admin' ? 'default' : 'secondary'}
                            >
                              {roleName.replace('_', ' ')}
                            </Badge>
                          );
                        })}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.is_active ? 'default' : 'secondary'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {user.created_at 
                        ? format(new Date(user.created_at), 'MMM d, yyyy')
                        : 'N/A'}
                    </TableCell>
                    {!isTeamLeader && (
                      <TableCell>
                        <Switch
                          checked={user.is_active}
                          onCheckedChange={(checked) => handleStatusChangeClick(user, checked)}
                          disabled={currentUser != null && (user.user_id === currentUser.id || user.id === currentUser.id)}
                        />
                      </TableCell>
                    )}
                    {!isTeamLeader && (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleDeleteUserClick(user)}
                          disabled={currentUser != null && (user.user_id === currentUser.id || user.id === currentUser.id)}
                          title="Delete user"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog open={pendingStatusChange != null} onOpenChange={(open) => !open && setPendingStatusChange(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingStatusChange?.newActive ? 'Activate user' : 'Deactivate user'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingStatusChange?.newActive
                ? `Are you sure you want to activate ${pendingStatusChange.user.name}? They will be able to sign in again.`
                : `Are you sure you want to deactivate ${pendingStatusChange?.user.name}? They will no longer be able to sign in.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingStatusChange(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmStatusChange}>
              {pendingStatusChange?.newActive ? 'Activate' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={isDeleteUserOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingUser(null);
            setTicketsToReassign([]);
            setTargetUserIdForReassign("");
          }
          setIsDeleteUserOpen(open);
        }}
      >
        <AlertDialogContent>
          {ticketsToReassign.length === 0 ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete user?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete {deletingUser?.name} ({deletingUser?.email}). This action cannot be undone.
                  If they have assigned tickets, you will need to reassign those tickets to another user first.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => { setDeletingUser(null); setIsDeleteUserOpen(false); }}>Cancel</AlertDialogCancel>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={deleteUser.isPending}
                  onClick={(e) => {
                    e.preventDefault();
                    handleConfirmDeleteUser();
                  }}
                >
                  {deleteUser.isPending && <span className="mr-2">Deleting...</span>}
                  Delete user
                </Button>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Reassign tickets to another user</AlertDialogTitle>
                <AlertDialogDescription>
                  {deletingUser?.name} has {ticketsToReassign.length} assigned ticket{ticketsToReassign.length !== 1 ? "s" : ""}. Choose a user to reassign them to, then the user will be deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-4 py-2">
                <div className="max-h-32 overflow-y-auto rounded border bg-muted/30 p-2 text-sm">
                  {ticketsToReassign.map((t) => (
                    <div key={t.id} className="flex justify-between gap-2 py-1">
                      <span className="font-medium">{t.ticketNumber}</span>
                      <span className="truncate text-muted-foreground">{t.description?.slice(0, 50)}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <Label>Reassign tickets to</Label>
                  <Select value={targetUserIdForReassign} onValueChange={setTargetUserIdForReassign}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select user..." />
                    </SelectTrigger>
                    <SelectContent>
                      {targetUsersForReassign.map((u) => (
                        <SelectItem key={u.id} value={u.user_id ?? u.id}>
                          {u.name} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel
                  onClick={() => {
                    setDeletingUser(null);
                    setTicketsToReassign([]);
                    setTargetUserIdForReassign("");
                    setIsDeleteUserOpen(false);
                  }}
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleReassignAndDeleteUser}
                  disabled={!targetUserIdForReassign || reassignUserTickets.isPending}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {reassignUserTickets.isPending && <span className="mr-2">Reassigning...</span>}
                  Reassign and delete user
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}